// Поточна станція SCADA — ОДНЕ джерело для всіх розділів.
//
// Станцію обирає глобальний селектор у топбарі; зберігається вона в
// useSelectedAsset (localStorage + подія синхронізації в межах вкладки). Тут ми
// нічого не дублюємо: лише перекладаємо вибраний актив на його SCADA-довідник
// наявною мапою ASSET_TO_MOCK і віддаємо сторінкам готові предикати.
//
// Дані в розділах SCADA прив'язані до станції по-різному: телеметрія має
// stationId, а події / команди СКО / журнал зміни — лише назву станції. Тому
// хук дає обидва матчери, і кожна сторінка фільтрує свої дані одним і тим
// самим контекстом, без власного стану станції.
import { useMemo } from 'react';
import { useSelectedAsset } from '@/hooks/useSelectedAsset';
import { scadaStationIdForAsset } from './useScopedStations';
import { STATIONS } from './mock/stations';
import { stationPath } from './navConfig';

/** Сентинел у списках навігації. Реальні маршрути — /scada/station/:id. */
export const SCADA_MONITORING = '/scada/station';

/** Чи веде цей шлях на картку станції SCADA (для підсвітки активного пункту). */
export const isScadaStationPath = (pathname: string) =>
  pathname.startsWith(`${SCADA_MONITORING}/`);

// Назви станції розходяться між джерелами: у селекторі «СЕС ЛУЧКИ», у
// SCADA-довіднику «Лучки». Нормалізація прибирає префікс, регістр і розділові
// знаки, далі порівнюємо на входження — цього досить для всіх шести СЕС.
const norm = (s: string) =>
  s.toLowerCase()
    .replace(/\bсес\b|\bses\b/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, '');

export interface ScadaStationContext {
  /** SCADA-ідентифікатор поточної станції (STATIONS[].id) або null. */
  id: string | null;
  /** Назва в SCADA-довіднику, напр. «Лучки». */
  name: string | null;
  /** Назва активу, як її показує селектор, напр. «СЕС ЛУЧКИ». */
  assetName: string | null;
  /** Підприємство-власник: контрагент активу, запасний — SCADA-довідник. */
  company: string | null;
  /** Куди веде «Моніторинг обладнання» просто зараз. */
  route: string;
  /** Рядок належить поточній станції? Без вибраної станції — пропускає всі. */
  matchesId: (stationId?: string | null) => boolean;
  /** Те саме для даних, у яких є лише назва станції. */
  matchesName: (stationName?: string | null) => boolean;
}

export function useScadaStation(): ScadaStationContext {
  const { selectedAsset } = useSelectedAsset();
  const id = scadaStationIdForAsset(selectedAsset?.id);
  const assetName = selectedAsset?.assetName ?? null;
  const counterparty = selectedAsset?.counterpartyShortName || selectedAsset?.counterpartyName || null;

  return useMemo(() => {
    const cfg = id ? STATIONS.find((s) => s.id === id) : undefined;
    const name = cfg?.name ?? null;
    // Усі написання поточної станції, які трапляються в даних.
    const keys = [name, assetName].filter(Boolean).map((s) => norm(s as string)).filter((s) => s.length > 1);

    return {
      id,
      name,
      assetName,
      company: counterparty ?? cfg?.legalEntity ?? null,
      // Якщо для активу немає SCADA-станції (або актив ще не вибрано) —
      // відкриваємо картку ПЕРШОЇ станції довідника, а не загальний перелік:
      // диспетчеру потрібен одразу екран станції (рішення 14.10).
      //
      // Спрацьовує це лише у вузьке вікно до відповіді /owner/stations: далі
      // селектор підставляє першу станцію зі скоупу акаунта. Чужих показників
      // так не видно — дані картки ріже scope на бекенді (/scada/overview
      // фільтрує станції за JWT), тож акаунт із вужчим доступом побачить
      // порожню картку, а не станцію поза своїм портфелем.
      route: stationPath(id ?? STATIONS[0].id),
      matchesId: (stationId?: string | null) => (id ? stationId === id : true),
      matchesName: (stationName?: string | null) => {
        if (keys.length === 0) return true;
        if (!stationName) return false;
        const v = norm(stationName);
        return keys.some((k) => v.includes(k) || k.includes(v));
      },
    };
  }, [id, assetName, counterparty]);
}

/** Скорочення для місць, яким потрібен лише маршрут «Моніторингу». */
export function useScadaStationRoute(): string {
  return useScadaStation().route;
}
